import { useEffect, useRef } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import List from "@editorjs/list";
import ImageTool from "@editorjs/image";
import CodeTool from "@editorjs/code";
import Quote from "@editorjs/quote";
import Delimiter from "@editorjs/delimiter";
import Table from "@editorjs/table";
import Embed from "@editorjs/embed";
import Marker from "@editorjs/marker";
import InlineCode from "@editorjs/inline-code";
import LinkTool from "@editorjs/link";

/**
 * EditorJS wrapper.
 *
 * Props:
 *  - value: string. Either an EditorJS JSON string, or legacy HTML, or empty.
 *  - onChange(jsonString): called with stringified EditorJS data on every save.
 *  - onImageUpload(file): async, returns absolute image URL.
 *  - flushRef: optional ref object; parent can call flushRef.current() to persist blocks before tab switch / save.
 *  - placeholder, readOnly, dir ("ltr" | "rtl")
 *  - editorKey: changing this key forces a full re-init (e.g. when switching articles or language)
 */
export default function EditorJsEditor({
  value,
  onChange,
  onImageUpload,
  flushRef,
  placeholder = "Start writing your article…",
  readOnly = false,
  dir = "ltr",
  editorKey,
}) {
  const holderRef = useRef(null);
  const editorRef = useRef(null);
  const onImageUploadRef = useRef(onImageUpload);
  const onChangeRef = useRef(onChange);
  onImageUploadRef.current = onImageUpload;
  onChangeRef.current = onChange;

  const parseInitial = (raw) => {
    if (!raw) return { blocks: [] };
    if (typeof raw === "object") return raw;
    const trimmed = String(raw).trim();
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && Array.isArray(parsed.blocks)) return parsed;
      } catch { /* fall through */ }
    }
    return {
      blocks: [{ type: "paragraph", data: { text: trimmed } }],
    };
  };

  const flushToParent = async (inst) => {
    if (!inst) return null;
    try {
      const data = await inst.save();
      const json = JSON.stringify(data);
      onChangeRef.current?.(json);
      return json;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!flushRef) return undefined;
    flushRef.current = async () => flushToParent(editorRef.current);
    return () => {
      flushRef.current = null;
    };
  }, [flushRef, editorKey]);

  useEffect(() => {
    if (!holderRef.current) return undefined;
    let editor = null;
    let cancelled = false;

    const timer = setTimeout(() => {
      if (cancelled || !holderRef.current) return;
      holderRef.current.innerHTML = "";
      const initialData = parseInitial(value);
      editor = new EditorJS({
        holder: holderRef.current,
        placeholder,
        readOnly,
        data: initialData,
        autofocus: false,
        minHeight: 300,
        tools: {
          header: { class: Header, inlineToolbar: true, config: { placeholder: "Heading", levels: [1, 2, 3, 4], defaultLevel: 2 } },
          paragraph: { class: Paragraph, inlineToolbar: true },
          list: { class: List, inlineToolbar: true, config: { defaultStyle: "unordered" } },
          quote: { class: Quote, inlineToolbar: true, config: { quotePlaceholder: "Enter a quote", captionPlaceholder: "Author" } },
          code: CodeTool,
          inlineCode: InlineCode,
          marker: Marker,
          delimiter: Delimiter,
          table: { class: Table, inlineToolbar: true, config: { rows: 3, cols: 3 } },
          embed: {
            class: Embed,
            config: { services: { youtube: true, vimeo: true, codepen: true, twitter: true, instagram: true } },
          },
          image: {
            class: ImageTool,
            config: {
              uploader: {
                uploadByFile: async (file) => {
                  try {
                    const url = await onImageUploadRef.current?.(file);
                    if (!url) return { success: 0 };
                    return { success: 1, file: { url } };
                  } catch (e) {
                    console.error("Image upload failed:", e);
                    return { success: 0 };
                  }
                },
                uploadByUrl: async (url) => ({ success: 1, file: { url } }),
              },
            },
          },
          linkTool: { class: LinkTool },
        },
        onChange: async () => {
          if (cancelled || !editorRef.current) return;
          await flushToParent(editorRef.current);
        },
      });
      editorRef.current = editor;
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      const inst = editor;
      const holderEl = holderRef.current;
      if (inst) {
        inst.isReady
          .then(async () => {
            await flushToParent(inst);
            try { inst.destroy?.(); } catch { /* ignore */ }
          })
          .catch(() => { /* ignore */ })
          .finally(() => {
            if (holderEl) holderEl.innerHTML = "";
            if (editorRef.current === inst) editorRef.current = null;
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorKey, readOnly]);

  return (
    <div
      dir={dir}
      className="editorjs-wrap border border-zinc-200 rounded-sm bg-white"
      data-testid="editorjs-editor"
    >
      <div ref={holderRef} className="px-6 py-4 editorjs-holder" />
    </div>
  );
}
